import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getTicketThread, replyAsUser } from "@/lib/support.functions";

export const Route = createFileRoute("/_authed/support_/$ticketId")({
  head: () => ({
    meta: [
      { title: "Ticket: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: TicketPage,
});

function fmt(iso: string): string {
  return new Date(iso).toLocaleString();
}

function TicketPage() {
  const { ticketId } = Route.useParams();
  const qc = useQueryClient();
  const threadFn = useServerFn(getTicketThread);
  const replyFn = useServerFn(replyAsUser);
  const [reply, setReply] = React.useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["support-thread", ticketId],
    queryFn: () => threadFn({ data: { ticketId } }),
  });

  const replyMut = useMutation({
    mutationFn: () =>
      replyFn({ data: { ticketId, message: reply.trim() } }),
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["support-thread", ticketId] });
      toast.success("Reply sent");
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Failed to send reply"),
  });

  if (isLoading) {
    return <div className="mx-auto max-w-3xl px-4 py-8">Loading…</div>;
  }
  if (error || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-destructive">Ticket not found.</p>
        <Link to="/support" className="underline">Back to support</Link>
      </div>
    );
  }

  const { ticket, messages } = data;
  const closed = ticket.status === "resolved" || ticket.status === "closed";

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <div className="mb-2">
        <Link to="/support" className="text-xs text-brand-muted underline">
          ← All tickets
        </Link>
      </div>
      <div className="mb-6 border-b border-brand-line pb-4">
        <h1 className="font-serif text-2xl text-brand-ink">{ticket.subject}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-brand-muted">
          {ticket.category ? (
            <Badge variant="outline" className="capitalize">{ticket.category}</Badge>
          ) : null}
          <Badge variant="outline" className="capitalize">
            {ticket.status.replace("_", " ")}
          </Badge>
          <span>Opened {fmt(ticket.created_at)}</span>
        </div>
      </div>

      <ul className="space-y-4">
        {messages.map((m) => {
          const isAdmin = m.author_role === "admin";
          return (
            <li
              key={m.id}
              className={
                isAdmin
                  ? "rounded-lg border border-brand-accent/40 bg-brand-accent/10 p-4"
                  : "rounded-lg border border-brand-line bg-brand-paper p-4"
              }
            >
              <div className="mb-1 flex items-center justify-between text-xs text-brand-muted">
                <span className="font-medium uppercase tracking-wider">
                  {isAdmin ? "CapitalDart" : "You"}
                </span>
                <span>{fmt(m.created_at)}</span>
              </div>
              <div className="whitespace-pre-wrap text-[14px] text-brand-ink">
                {m.body}
              </div>
            </li>
          );
        })}
      </ul>

      {closed ? (
        <div className="mt-6 rounded-lg border border-brand-line bg-brand-paper-2 p-4 text-sm text-brand-muted">
          This ticket is {ticket.status}. Open a new one if you have additional questions.
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reply.trim().length > 0 && !replyMut.isPending) replyMut.mutate();
          }}
          className="mt-6 space-y-3"
        >
          <label className="block text-xs font-medium uppercase tracking-wider text-brand-muted">
            Reply
          </label>
          <Textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={5}
            maxLength={10000}
            placeholder="Add to this ticket…"
          />
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={reply.trim().length === 0 || replyMut.isPending}
            >
              {replyMut.isPending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}